import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext, SttDialog, SttForm } from '../../../sporttia/all';
import {
    lockFormElements,
    useCanModifyConfiguration,
} from './useCanModifyConfiguration';

type ChargingOption = {
    id?: number;
    amount?: number;
    bonus?: number;
    name?: string;
};

type Props = {
    chargingOption: ChargingOption | null;
    onClose: () => void;
};

export default function ConfigurationRechargingOptionDialog({
    chargingOption = null,
    onClose,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const canModify = useCanModifyConfiguration();
    const [internalChargingOption, setInternalChargingOption] = useState<
        ChargingOption | undefined
    >();

    /**
     * Charing options changes
     */
    useEffect(() => {
        setInternalChargingOption(chargingOption || undefined);
    }, [chargingOption]);

    /**
     * Save charging option
     */
    const saveChargingOption = (): void => {
        // Updating
        if (chargingOption?.id) {
            cxt?.api('PUT', `/chargings/options/${chargingOption.id}`, {
                params: {
                    amount: internalChargingOption?.amount,
                    bonus: internalChargingOption?.bonus,
                },
                success: () => {
                    onClose();
                },
            });

            // Creating
        } else {
            cxt?.api('POST', `/scs/${cxt?.sc?.id}/chargings/options`, {
                params: {
                    amount: internalChargingOption?.amount,
                    bonus: internalChargingOption?.bonus,
                },
                success: () => {
                    onClose();
                },
            });
        }
    };

    /**
     * Handle form
     */
    const handleForm = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void => {
        if (!name) return;
        // Convert string values to numbers for amount and bonus
        const processedValue =
            (name === 'amount' || name === 'bonus') && typeof value === 'string'
                ? parseFloat(value) || undefined
                : value;
        setInternalChargingOption({
            ...internalChargingOption,
            [name]: processedValue,
        });
    };

    // -----| Render |-----
    return (
        <SttDialog
            title={t('Recharge')}
            open={chargingOption !== null}
            onClose={onClose}
            content={
                <>
                    {internalChargingOption && (
                        <SttForm
                            onChangeForm={handleForm}
                            elements={lockFormElements(
                                [
                                    {
                                        md: 6,
                                        caption: t('Amount'),
                                        name: 'amount',
                                        type: 'number',
                                        value: internalChargingOption.amount?.toString(),
                                    },
                                    {
                                        md: 6,
                                        caption: t('Bonus'),
                                        name: 'bonus',
                                        type: 'number',
                                        value: internalChargingOption.bonus?.toString(),
                                    },
                                ],
                                !canModify,
                            )}
                        />
                    )}
                </>
            }
            buttons={[
                {
                    type: 'save',
                    show: canModify,
                    onClick: () => saveChargingOption(),
                },
                {
                    type: 'close',
                    onClick: onClose,
                },
            ]}
        />
    );
}
